/**
 * What a theme is, minus its React.
 *
 * Every theme ships one of these - `previewThemes/<id>/meta.ts` - holding the
 * design constants and the words that describe it, and nothing that imports a
 * component. Two reasons it is split from `theme.tsx`:
 *
 * - `features/storefront/theme.ts` turns tokens into CSS custom properties,
 *   and a theme's own components import *that*. Reading tokens from a module
 *   with components in it would close the circle.
 * - The merchant's theme gallery and settings screens need every theme's name,
 *   palette and tokens, and none of their markup. This is the module they read.
 */

import type { ImageSpecOverrides } from '@/lib/imageSpecs';

export interface ThemeTokens {
  /** Fallback heading face when the merchant has not chosen one. */
  headingFont: string | null;
  /**
   * A second colour the theme paints its own details with - a highlighted
   * link, an in-stock note, a discount line. `null` means the theme has no
   * such colour and those details take the merchant's primary.
   *
   * Not a merchant setting: it belongs to the theme's design the way its
   * tracking does, and a palette of five colours a merchant already has to
   * keep coherent does not need a sixth.
   */
  accent: string | null;
  /**
   * The face this theme sets its machine details in - a price, a SKU, a
   * countdown, a small-caps eyebrow. `null` means the theme has no such face
   * and those details take the body font.
   */
  monoFont: string | null;
  headingTracking: string;
  headingTransform: 'none' | 'uppercase';
  /**
   * A second face for the word or two a heading leans on - Feast sets one word
   * of every heading in a brush script and the rest in its own sans.
   *
   * Which words is the *document's* business, not the theme's: a heading marks
   * them with asterisks (`'Shop by *Categories*'`) and the renderer swaps the
   * face for what is between them. Omitted - which is every theme but one -
   * the variable resolves to `inherit`, the markers are still stripped, and a
   * heading renders exactly as it always has.
   */
  displayFont?: string | null;
  /**
   * Where a section's heading stands over the section it introduces. Omitted
   * is `'left'`, which is where every heading stood before this existed.
   */
  headingAlign?: 'left' | 'center';
  /**
   * How a full-width hero banner ends at the bottom. Omitted is `'straight'`,
   * which is every banner before this existed. `'slant'` cuts the banner on a
   * diagonal - higher on the left than the right - so the page's own colour
   * rises into the photograph like a track banking into a bend.
   */
  heroEdge?: 'straight' | 'slant';
  /**
   * Whether product cards show their star rating. Left off, a card is its
   * picture, name and price, and the stars stay on the product page.
   */
  cardRatings?: boolean;
  /**
   * How a coloured section and the footer meet the page. Omitted is
   * `'full'` - a band across the window, as every theme had before this.
   * `'card'` keeps them to the page column as rounded cards.
   */
  bandFrame?: 'full' | 'card';
  /** Page width. The base 72rem is Tailwind's max-w-6xl. */
  containerMax: string;
  /** Section padding multiplier; 1 reproduces the base spacing exactly. */
  spaceScale: number;
  /**
   * Roughly how tall this theme's header stands, which is what a full-screen
   * banner subtracts from the window. Approximate on purpose: a header grows
   * with the merchant's nav row, and a banner a few pixels short of the fold
   * is not worth measuring the header on every paint.
   */
  headerHeight: string;
}

/**
 * Which written-down shop a theme is previewed with.
 *
 * A name, not the catalog itself. This module is globbed eagerly so the
 * gallery can list every theme, and a theme that imported its catalog here
 * would pull two hundred products into the bundle of a screen that shows a
 * swatch and a sentence. `catalog/index.ts` resolves the name when a preview
 * is actually opened.
 */
export type PreviewCatalogName =
  | 'general'
  | 'fashion'
  | 'electronics'
  | 'home'
  | 'beauty'
  | 'grocery';

/**
 * The palette and type a theme is *designed* in.
 *
 * A merchant's own theme document carries colours they have chosen, and the
 * storefront reads them from there. A preview has no merchant, so these are
 * what it wears: the look the theme was drawn in, which is the look the
 * gallery's swatches show and the look the preview opens on.
 *
 * They are also what a merchant gets when they apply the theme, before they
 * change anything - so this is the theme's opinion about itself, not a
 * placeholder.
 */
export interface ThemeLook {
  colors: {
    primary: string;
    background: string;
    surface: string;
    text: string;
    muted: string;
  };
  typography: {
    font_family: string;
    heading_font?: string | null;
    heading_weight: '500' | '600' | '700' | '800';
    base_size_px?: number;
  };
  buttons: {
    radius_px: number;
    style: 'solid' | 'outline';
  };
  /** The shop this theme is shown with. `general` when it says nothing. */
  catalog?: PreviewCatalogName;
}

/** How a theme reads to a merchant choosing one. */
export interface ThemeMeta {
  label: string;
  hint: string;
  /**
   * The industries this theme was drawn for, as industry keys (`clinics`,
   * `fashion_apparel`; `general` for any shop) - the gallery's filter chips.
   * Shown by name through `industryName`.
   */
  industries?: string[];
  /** What the theme looks like with nothing customised. See {@link ThemeLook}. */
  look: ThemeLook;
  /**
   * The image slots this theme draws in a different shape from the
   * storefront's defaults in `lib/imageSpecs.ts`, laid over them by
   * `imageSpecFor`.
   *
   * Only what the theme's rendering actually changes belongs here - each entry
   * names the component that draws it. A theme that draws a slot the default
   * way says nothing, so a correction to a default reaches it too.
   */
  imageSpecs?: ImageSpecOverrides;
}

interface MetaModule {
  tokens: ThemeTokens;
  meta: ThemeMeta;
}

/**
 * Every theme this build ships, discovered by folder.
 *
 * Eager because the storefront shell needs the active theme's tokens on its
 * first paint - a lazily-loaded palette is a flash of the wrong shop.
 */
const modules = import.meta.glob<MetaModule>('./*/meta.ts', { eager: true });

function folderId(path: string): string {
  // './voltic/meta.ts' -> 'voltic'
  return path.split('/')[1];
}

/**
 * The theme every other theme falls back to, and the one a document names when
 * it names nothing. Its tokens are calibrated to render the storefront exactly
 * as it did before themes were packages.
 */
export const BASE_THEME_ID = 'classic';

/** Every theme's id, the base first and the rest alphabetical - a stable gallery. */
export const THEME_IDS: string[] = Object.keys(modules)
  .map(folderId)
  .sort((a, b) => {
    if (a === BASE_THEME_ID) return -1;
    if (b === BASE_THEME_ID) return 1;
    return a.localeCompare(b);
  });

export const THEME_TOKENS: Record<string, ThemeTokens> = Object.fromEntries(
  Object.entries(modules).map(([path, module]) => [folderId(path), module.tokens]),
);

export const THEME_META: Record<string, ThemeMeta> = Object.fromEntries(
  Object.entries(modules).map(([path, module]) => [folderId(path), module.meta]),
);

/**
 * Every theme's preview shot, discovered the same way its meta is.
 *
 * A theme drops a `<id>.webp` beside its `meta.ts` and the gallery shows it -
 * no list to keep in step with the folders. Globbed as URLs rather than as
 * modules, so what lands in the bundle is a string per theme and the images
 * themselves stay files the browser fetches when a card scrolls into view.
 *
 * A theme adding or changing its shot also runs
 * `scripts/generate-card-shots.py`, which makes the narrower copies and the
 * placeholder the cards draw it with (`cardShots.ts`).
 */
const previews = import.meta.glob<string>('./*/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
});

export const THEME_PREVIEWS: Record<string, string> = {};

for (const [path, url] of Object.entries(previews)) {
  // './voltic/voltic.webp'. A stray second shot beside `meta.ts` never wins
  // over `<id>.webp`: which one a card shows must not depend on glob order.
  const id = folderId(path);
  if (!THEME_PREVIEWS[id] || path.endsWith(`/${id}.webp`)) THEME_PREVIEWS[id] = url;
}

/**
 * A theme's preview shot, or `null` when it has not been shot yet.
 *
 * Deliberately not falling back to the base theme the way tokens and meta do:
 * a picture of *another* theme's shop is worse than no picture, and the
 * gallery has its swatch to fall back to.
 */
export function themePreview(id: string): string | null {
  return THEME_PREVIEWS[id] ?? null;
}

/**
 * A theme's tokens, or the base theme's if this build does not ship it.
 *
 * Falling back rather than throwing is deliberate: theme documents are stored
 * data, and a merchant whose saved theme names something this build does not
 * have - a theme withdrawn, or a document restored from a newer deploy - gets
 * their shop in the base look rather than a blank page.
 */
export function themeTokens(id: string): ThemeTokens {
  return THEME_TOKENS[id] ?? THEME_TOKENS[BASE_THEME_ID];
}

export function themeMeta(id: string): ThemeMeta {
  return THEME_META[id] ?? THEME_META[BASE_THEME_ID];
}

/** The palette and type a theme is designed in, falling back like the rest. */
export function themeLook(id: string): ThemeLook {
  return themeMeta(id).look;
}

/**
 * How a theme reshapes the storefront's image slots - empty for a theme that
 * draws every slot the default way, and for one this build does not ship,
 * which then renders in the base theme and so takes the base theme's shapes.
 */
export function themeImageSpecs(id: string): ImageSpecOverrides {
  return themeMeta(id).imageSpecs ?? {};
}

export function isKnownTheme(id: string): boolean {
  return id in THEME_TOKENS;
}

/**
 * Every industry some theme claims, alphabetically.
 *
 * Derived rather than declared, so the gallery's filter chips are always
 * exactly the industries a merchant can actually filter down to something with
 * - a chip that returns nothing is worse than no chip.
 */
export const THEME_INDUSTRIES: string[] = [
  ...new Set(Object.values(THEME_META).flatMap((meta) => meta.industries ?? [])),
].sort((a, b) => a.localeCompare(b));

/** The themes drawn for one industry, or all of them for `null`. */
export function themesForIndustry(industry: string | null): string[] {
  if (!industry) return THEME_IDS;
  return THEME_IDS.filter((id) => (themeMeta(id).industries ?? []).includes(industry));
}
